import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useMutation, useLazyQuery } from '@apollo/client'
import {
    ArrowLeftIcon,
    CheckIcon,
    CheckCircleIcon,
} from '@heroicons/react/20/solid'
import { CalendarIcon } from '@heroicons/react/24/outline'
import { BoatIcon } from 'components/icons'
import {
    customDateFormat,
    customTimeFormat,
} from 'utils/common'
import { t } from 'i18next'
import { 
    PASSENGER_COUNTER_CHECKIN, 
    UPDATE_BOOKING_PASSENGER_LUGGAGE, 
    COMPLETE_PASSENGER_COUNTER_CHECKIN,
    ADD_PASSENGER_LUGGAGE,
    REMOVE_PASSENGER_LUGGAGE,
    PRINT_PASSENGER_LUGGAGE_TAG
} from 'graphql/operations/mutations'
import { GET_CHECKIN_STATUS, GET_LUGGAGE_STATUS, GET_LUGGAGE_TAG } from 'graphql/operations/queries'
import { ErrorDialog, SuccessDialog, ConfirmDialog, ErrorToast } from 'components/custom/ResponseDialog'
import { BookingSummary } from 'components/custom/BookingSummary'
import { PassengerCard } from 'components/custom/PassengerCard'
import { LuggageDetailsForm } from 'components/custom/LuggageDetailsForm'
import { printLuggageTag, isBrowserPrintAvailable } from 'utils/zebraPrinter'

/**
 * CheckInPage Component
 * 
 * Performance Optimizations:
 * - useCallback: All event handlers memoized to prevent unnecessary re-renders
 * - useMemo: Computed values (currentSegment, tabs) cached for better performance
 * - React.memo: LuggageDetailsForm component wrapped to prevent re-renders
 * 
 * Design Consistency:
 * - Uses standard ConfirmDialog component (from ResponseDialog)
 * - Consistent button styling with bg-primary and hover:bg-primary/90
 * - Barcode (not QR code) for luggage tags to match requirements
 * 
 * Note: Passenger tickets still use QR codes (in TicketActions component)
 */
export default function CheckInPage() {
    const location = useLocation()
    const navigate = useNavigate()
    const { bookingInfo, passengerId, segmentId, travelDate, bookingId } = location.state || {}
    
    const [selectedPassengers, setSelectedPassengers] = useState([])
    const [luggageCounts, setLuggageCounts] = useState({})
    const [remarks, setRemarks] = useState('')
    const [counterNo, setCounterNo] = useState('')
    const [checkinStatuses, setCheckinStatuses] = useState([])
    const [luggageStatuses, setLuggageStatuses] = useState([])
    const [luggageTags, setLuggageTags] = useState([])
    const [checkinResults, setCheckinResults] = useState(null)
    const [activeTab, setActiveTab] = useState('passengers') // 'passengers', 'luggage', 'complete'
    const [isViewMode, setIsViewMode] = useState(false) // Track if we're viewing existing check-in data
    
    const [showSucDialog, setShowSucDialog] = useState(false)
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [showErrToast, setShowErrToast] = useState(false)
    const [dialogMessages, setDialogMessages] = useState('')
    const [toastMessages, setToastMessages] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [luggageDetails, setLuggageDetails] = useState({}) // Store luggage details for each luggage item
    const [showConfirmDialog, setShowConfirmDialog] = useState(false)
    const [removingLuggageId, setRemovingLuggageId] = useState(null)
    const [showRemoveConfirmDialog, setShowRemoveConfirmDialog] = useState(false)
    const [printingLuggageId, setPrintingLuggageId] = useState(null)
    const [savedLuggageDetails, setSavedLuggageDetails] = useState({}) // Track saved luggage details to detect modifications
    
    // Get the specific trip segment for check-in
    const getCurrentTripSegment = () => {
        if (!bookingInfo?.trip_segments) return null
        
        // If segmentId is provided, find that specific segment
        if (segmentId) {
            return bookingInfo.trip_segments.find(segment => segment.segment_id === segmentId)
        }
        
        // If passengerId is provided, find the segment containing that passenger
        if (passengerId) {
            return bookingInfo.trip_segments.find(segment => 
                segment.passengers.some(p => p.passenger_id === passengerId)
            )
        }
        
        // Default to first segment
        return bookingInfo.trip_segments[0]
    }
    
    const currentSegment = useMemo(() => getCurrentTripSegment(), [bookingInfo, segmentId, passengerId])

    // Get check-in statuses
    const [getCheckinStatuses] = useLazyQuery(GET_CHECKIN_STATUS, {
        onCompleted: (data) => {
            if (data?.checkinStatus) {
                setCheckinStatuses(Array.isArray(data.checkinStatus) ? data.checkinStatus : [data.checkinStatus])
            }
        },
        onError: (error) => {
            console.error('Error fetching check-in statuses:', error)
        }
    })

    // Get luggage statuses
    const [getLuggageStatuses] = useLazyQuery(GET_LUGGAGE_STATUS, {
        onCompleted: (data) => {
            if (data?.luggageStatus) {
                setLuggageStatuses(Array.isArray(data.luggageStatus) ? data.luggageStatus : [data.luggageStatus])
            }
        },
        onError: (error) => {
            console.error('Error fetching luggage statuses:', error)
        }
    })

    // Get luggage tags
    const [getLuggageTags] = useLazyQuery(GET_LUGGAGE_TAG, {
        onCompleted: (data) => {
            if (data?.luggageTag) {
                setLuggageTags(Array.isArray(data.luggageTag) ? data.luggageTag : [data.luggageTag])
            }
        },
        onError: (error) => {
            console.error('Error fetching luggage tags:', error)
        }
    })


    // Passenger check-in mutation
    const [passengerCheckin, { loading: checkinLoading }] = useMutation(PASSENGER_COUNTER_CHECKIN, {
        onCompleted: (data) => {
            if (data?.passengerCounterCheckin?.success) {
                setCheckinResults(data.passengerCounterCheckin.checkins)
                setActiveTab('luggage') // Move to luggage details tab
                setDialogMessages(data.passengerCounterCheckin.message)
                setShowSucDialog(true)
                setIsLoading(false)
            } else {
                setDialogMessages(data?.passengerCounterCheckin?.message || t('Check-in failed'))
                setShowErrDialog(true)
                setIsLoading(false)
            }
        },
        onError: (error) => {
            setDialogMessages(error.message || t('Check-in failed'))
            setShowErrDialog(true)
            setIsLoading(false)
        }
    })

    // Add luggage mutation
    const [addLuggage, { loading: addLuggageLoading }] = useMutation(ADD_PASSENGER_LUGGAGE, {
        onCompleted: (data) => {
            if (data?.addPassengerLuggage?.success) {
                // Update the checkinResults state with the new luggage
                setCheckinResults(prevResults => {
                    return prevResults.map(checkin => {
                        if (checkin.checkin_id === data.addPassengerLuggage.luggage.checkin_id) {
                            return {
                                ...checkin,
                                luggages: [...(checkin.luggages || []), data.addPassengerLuggage.luggage]
                            }
                        }
                        return checkin
                    })
                })
                setDialogMessages(data.addPassengerLuggage.message)
                setShowSucDialog(true)
            } else {
                setDialogMessages(data?.addPassengerLuggage?.message || t('Failed to add luggage'))
                setShowErrDialog(true)
            }
        },
        onError: (error) => {
            setDialogMessages(error.message || t('Failed to add luggage'))
            setShowErrDialog(true)
        }
    })

    // Remove luggage mutation
    const [removeLuggage, { loading: removeLuggageLoading }] = useMutation(REMOVE_PASSENGER_LUGGAGE, {
        onCompleted: (data) => {
            if (data?.removePassengerLuggage?.success) {
                // Update the checkinResults state by removing the luggage
                setCheckinResults(prevResults => {
                    return prevResults.map(checkin => {
                        return {
                            ...checkin,
                            luggages: checkin.luggages.filter(l => l.luggage_id !== removingLuggageId)
                        }
                    })
                })
                setDialogMessages(data.removePassengerLuggage.message)
                setShowSucDialog(true)
            } else {
                setDialogMessages(data?.removePassengerLuggage?.message || t('Failed to remove luggage'))
                setShowErrDialog(true)
            }
        },
        onError: (error) => {
            setDialogMessages(error.message || t('Failed to remove luggage'))
            setShowErrDialog(true)
        }
    })

    // Update luggage mutation
    const [updatingLuggageId, setUpdatingLuggageId] = useState(null)
    const [updateLuggage, { loading: updateLuggageLoading }] = useMutation(UPDATE_BOOKING_PASSENGER_LUGGAGE, {
        onCompleted: (data) => {
            if (data?.updateBookingPassengerLuggage?.success) {
                const updatedLuggageId = data.updateBookingPassengerLuggage.luggage.luggage_id
                // Update the checkinResults state with the new luggage status
                setCheckinResults(prevResults => {
                    return prevResults.map(checkin => {
                        const updatedLuggages = checkin.luggages.map(luggage => {
                            if (luggage.luggage_id === updatedLuggageId) {
                                // Return updated luggage with new status
                                return {
                                    ...luggage,
                                    status: data.updateBookingPassengerLuggage.luggage.status
                                }
                            }
                            return luggage
                        })
                        return {
                            ...checkin,
                            luggages: updatedLuggages
                        }
                    })
                })
                // Save the current luggage details as the saved state
                setSavedLuggageDetails(prev => ({
                    ...prev,
                    [updatedLuggageId]: JSON.stringify(luggageDetails[updatedLuggageId] || {})
                }))
                setDialogMessages(data.updateBookingPassengerLuggage.message)
                setShowSucDialog(true)
            } else {
                setDialogMessages(data?.updateBookingPassengerLuggage?.message || t('Luggage update failed'))
                setShowErrDialog(true)
            }
        },
        onError: (error) => {
            setDialogMessages(error.message || t('Luggage update failed'))
            setShowErrDialog(true)
        }
    })

    // Print luggage tag mutation
    const [printLuggageTagMutation, { loading: printLuggageLoading }] = useMutation(PRINT_PASSENGER_LUGGAGE_TAG, {
        onCompleted: async (data) => {
            if (data?.printPassengerLuggageTag?.success && data?.printPassengerLuggageTag?.zplData) {
                try {
                    // Check if Browser Print is available
                    if (!isBrowserPrintAvailable()) {
                        setToastMessages('Zebra Browser Print is not available. Please ensure the Browser Print application is installed and running on your computer.')
                        setShowErrToast(true)
                        setPrintingLuggageId(null)
                        return
                    }

                    // Send ZPL data to Zebra printer
                    await printLuggageTag(data.printPassengerLuggageTag.zplData)
                    
                    setDialogMessages(data.printPassengerLuggageTag.message || 'Luggage tag printed successfully')
                    setShowSucDialog(true)
                } catch (error) {
                    console.error('Print error:', error)
                    if (error.message.includes('Browser Print')) {
                        setToastMessages(`Failed to print luggage tag: ${error.message}`)
                        setShowErrToast(true)
                    } else {
                        setDialogMessages(`Failed to print luggage tag: ${error.message}`)
                        setShowErrDialog(true)
                    }
                } finally {
                    setPrintingLuggageId(null)
                }
            } else {
                setDialogMessages(data?.printPassengerLuggageTag?.message || 'Failed to retrieve luggage tag data')
                setShowErrDialog(true)
                setPrintingLuggageId(null)
            }
        },
        onError: (error) => {
            console.error('Print API error:', error)
            setDialogMessages(`Failed to print luggage tag: ${error.message}`)
            setShowErrDialog(true)
            setPrintingLuggageId(null)
        }
    })

    // Complete check-in mutation
    const [completeCheckin, { loading: completeCheckinLoading }] = useMutation(COMPLETE_PASSENGER_COUNTER_CHECKIN, {
        onCompleted: (data) => {
            if (data?.completePassengerCounterCheckin?.success) {
                setDialogMessages(data.completePassengerCounterCheckin.message)
                setShowSucDialog(true)
                setShowConfirmDialog(false)
                // Navigate back to manage booking after a delay
                setTimeout(() => {
                    navigate('/check-in')
                }, 2000)
            } else {
                setDialogMessages(data?.completePassengerCounterCheckin?.message || t('Failed to complete check-in'))
                setShowErrDialog(true)
                setShowConfirmDialog(false)
            }
        },
        onError: (error) => {
            setDialogMessages(error.message || t('Failed to complete check-in'))
            setShowErrDialog(true)
            setShowConfirmDialog(false)
        }
    })

    useEffect(() => {
        if (!bookingInfo) {
            navigate('/check-in')
            return
        }
        
        // Check if any passengers in the current segment have existing check-in data
        if (currentSegment?.passengers) {
            const checkedInPassengers = currentSegment.passengers.filter(p => p.check_in !== null && p.check_in !== undefined)
            
            if (checkedInPassengers.length > 0) {
                // View mode - auto-populate existing check-in data
                setIsViewMode(true)
                
                // Pre-select all checked-in passengers
                const checkedInPassengerIds = checkedInPassengers.map(p => p.passenger_id)
                setSelectedPassengers(checkedInPassengerIds)
                
                // Build luggage counts from existing check-in data
                const luggageCountsMap = {}
                checkedInPassengers.forEach(p => {
                    luggageCountsMap[p.passenger_id] = p.check_in?.luggage_count || 0
                })
                setLuggageCounts(luggageCountsMap)
                
                // Populate counter number and remarks from first check-in
                const firstCheckin = checkedInPassengers[0].check_in
                if (firstCheckin) {
                    setCounterNo(firstCheckin.details?.counter_no || '')
                    setRemarks(firstCheckin.details?.remarks || '')
                    
                    // Build checkin results for luggage tab
                    const existingCheckins = checkedInPassengers.map(p => ({
                        checkin_id: p.check_in.checkin_id,
                        passenger_id: p.passenger_id,
                        luggage_count: p.check_in.luggage_count || 0,
                        status: p.check_in.status,
                        luggages: p.check_in.luggages || []
                    }))
                    setCheckinResults(existingCheckins)
                    
                    // Auto-populate luggage details if they exist
                    const luggageDetailsMap = {}
                    checkedInPassengers.forEach(p => {
                        if (p.check_in?.luggages) {
                            p.check_in.luggages.forEach(luggage => {
                                if (luggage.details) {
                                    luggageDetailsMap[luggage.luggage_id] = {
                                        weight: luggage.details.weight || '',
                                        length: luggage.details.dimensions?.length || '',
                                        width: luggage.details.dimensions?.width || '',
                                        height: luggage.details.dimensions?.height || '',
                                        tags: luggage.details.tags || []
                                    }
                                }
                            })
                        }
                    })
                    setLuggageDetails(luggageDetailsMap)
                    
                    // Switch to luggage tab if there are luggages
                    if (existingCheckins.some(c => c.luggage_count > 0)) {
                        setActiveTab('luggage')
                    }
                }
            } else {
                // New check-in mode
                setIsViewMode(false)
                
                // Initialize selected passengers with the specific passenger if provided
                if (passengerId) {
                    const passenger = findPassengerById(passengerId)
                    if (passenger) {
                        setSelectedPassengers([passengerId])
                        setLuggageCounts({ [passengerId]: 0 })
                    }
                }
            }
        }
        
        // Fetch check-in statuses and lookup data
        getCheckinStatuses({ variables: { code: null } })
        getLuggageStatuses({ variables: { code: null } })
        getLuggageTags({ variables: { code: null } })
    }, [bookingInfo, passengerId, currentSegment])

    const findPassengerById = useCallback((id) => {
        if (!currentSegment?.passengers) return null
        return currentSegment.passengers.find(p => p.passenger_id === id)
    }, [currentSegment])
    
    const handlePassengerToggle = useCallback((passengerId) => {
        setSelectedPassengers(prev => {
            if (prev.includes(passengerId)) {
                // Remove passenger
                const newLuggageCounts = { ...luggageCounts }
                delete newLuggageCounts[passengerId]
                setLuggageCounts(newLuggageCounts)
                return prev.filter(id => id !== passengerId)
            } else {
                // Add passenger
                setLuggageCounts(prev => ({ ...prev, [passengerId]: 0 }))
                return [...prev, passengerId]
            }
        })
    }, [luggageCounts])

    const handleLuggageChange = useCallback((passengerId, count) => {
        setLuggageCounts(prev => ({ ...prev, [passengerId]: Math.max(0, count) }))
    }, [])


    const handleStartCheckIn = () => {
        if (selectedPassengers.length === 0) {
            setDialogMessages(t('Please select at least one passenger'))
            setShowErrDialog(true)
            return
        }

        setIsLoading(true)
        
        // Prepare passenger IDs with luggage count according to API spec
        const passengerIds = selectedPassengers.map(passengerId => ({
            passenger_id: passengerId,
            luggage_count: luggageCounts[passengerId] || 0
        }))

        const checkinDetails = {
            booking_id: bookingId,
            segment_id: segmentId,
            passenger_ids: passengerIds,
            travel_date: currentSegment?.travel_date || travelDate,
            checkin_details: {
                remarks: remarks.trim() || 'Checked in at counter',
                counter_no: counterNo
            }
        }

        passengerCheckin({
            variables: {
                passengerCheckinDetails: checkinDetails
            }
        })
    }

    const handleBack = () => {
        if (activeTab === 'passengers') {
            // Navigate back to booking details page using the current segment's travel date
            const segmentTravelDate = currentSegment?.travel_date || travelDate
            navigate(`/manage-boat-booking/details/${bookingId}/${segmentTravelDate}`, {
                state: { 
                    filterObj: null, 
                    isCheckInMode: true 
                }
            })
        } else if (activeTab === 'luggage') {
            // Go back to passengers tab
            setActiveTab('passengers')
        }
    }

    const handleLuggageDetailsChange = useCallback((luggageId, field, value) => {
        setLuggageDetails(prev => ({
            ...prev,
            [luggageId]: {
                ...prev[luggageId],
                [field]: value
            }
        }))
    }, [])

    const handleUpdateLuggage = useCallback((luggage) => {
        const details = luggageDetails[luggage.luggage_id] || {}
        setUpdatingLuggageId(luggage.luggage_id)
        
        const luggageInput = {
            booking_id: luggage.booking_id,
            checkin_id: luggage.checkin_id,
            luggage_id: luggage.luggage_id,
            status: luggage.status?.code || 'ACC',
            details: {
                weight: parseFloat(details.weight) || 0,
                dimensions: {
                    length: parseFloat(details.length) || 0,
                    width: parseFloat(details.width) || 0,
                    height: parseFloat(details.height) || 0
                },
                color: '',
                tags: details.tags || []
            }
        }

        updateLuggage({
            variables: {
                luggage: luggageInput
            }
        }).finally(() => {
            setUpdatingLuggageId(null)
        })
    }, [updateLuggage, luggageDetails])

    const handleCompleteCheckIn = useCallback(() => {
        // Show confirmation dialog to finalize check-in
        setShowConfirmDialog(true)
    }, [])

    const confirmCompleteCheckin = useCallback(() => {
        if (!bookingId || !currentSegment?.travel_date || !currentSegment?.trip_id) {
            setDialogMessages(t('Missing required information to complete check-in'))
            setShowErrDialog(true)
            setShowConfirmDialog(false)
            return
        }

        completeCheckin({
            variables: {
                bookingId: bookingId,
                travelDate: currentSegment.travel_date,
                tripId: currentSegment.trip_id
            }
        })
    }, [completeCheckin, bookingId, currentSegment])

    // Check if ALL luggage items have reached ACCEPTED status (ACC) and check-in status
    const { allLuggageAccepted, isCheckedIn } = useMemo(() => {
        if (!checkinResults) return { allLuggageAccepted: false, isCheckedIn: false }
        
        // Get all luggage items from all check-ins
        const allLuggages = checkinResults.flatMap(checkin => checkin.luggages || [])
        
        // Check if any check-in has CHK status
        const hasCheckedInStatus = checkinResults.some(checkin => checkin.status?.code === 'CHK')
        
        // If no luggage items, allow completion (luggage count is 0)
        if (allLuggages.length === 0) {
            return { 
                allLuggageAccepted: true, 
                isCheckedIn: hasCheckedInStatus 
            }
        }
        
        // Check if ALL luggage items have reached ACCEPTED status
        return { 
            allLuggageAccepted: allLuggages.every(luggage => luggage.status?.code === 'ACC'),
            isCheckedIn: hasCheckedInStatus
        }
    }, [checkinResults])

    const tabs = useMemo(() => [
        { key: 'passengers', name: t('Passenger Check-In'), disabled: false },
        { key: 'luggage', name: t('Luggage Details'), disabled: !checkinResults }
    ], [checkinResults])

    const handleTabClick = useCallback((tabKey) => {
        const tab = tabs.find(t => t.key === tabKey)
        if (!tab?.disabled) {
            setActiveTab(tabKey)
        }
    }, [tabs])

    const handleAddLuggage = useCallback((luggage) => {
        addLuggage({
            variables: {
                booking_id: luggage.booking_id,
                checkin_id: luggage.checkin_id
            }
        })
    }, [addLuggage])

    const handleRemoveLuggage = useCallback((luggage) => {
        setRemovingLuggageId(luggage.luggage_id)
        setShowRemoveConfirmDialog(true)
    }, [])

    const confirmRemoveLuggage = useCallback(() => {
        if (!removingLuggageId) return

        const luggage = checkinResults
            ?.flatMap(checkin => checkin.luggages)
            ?.find(l => l.luggage_id === removingLuggageId)

        if (!luggage) return

        removeLuggage({
            variables: {
                booking_id: luggage.booking_id,
                checkin_id: luggage.checkin_id,
                luggage_id: luggage.luggage_id
            }
        })
        setShowRemoveConfirmDialog(false)
    }, [removeLuggage, removingLuggageId, checkinResults])

    const handlePrintLuggage = useCallback((luggage) => {
        if (!luggage?.booking_id || !luggage?.checkin_id || !luggage?.luggage_id) {
            setDialogMessages('Invalid luggage information')
            setShowErrDialog(true)
            return
        }

        setPrintingLuggageId(luggage.luggage_id)
        
        // Call API to get ZPL data and print
        printLuggageTagMutation({
            variables: {
                bookingId: luggage.booking_id,
                checkinId: luggage.checkin_id,
                luggageId: luggage.luggage_id
            }
        })
    }, [printLuggageTagMutation])

    if (!bookingInfo) {
        return (
            <div className="flex justify-center items-center h-96">
                <div className="text-lg text-center">{t('No booking information found')}</div>
            </div>
        )
    }


    return (
        <>
            <div className="mb-4 flex justify-normal items-center">
                <div className="flex flex-wrap items-center gap-3 sm:flex-nowrap justify-between">
                    <button
                        onClick={handleBack}
                        type="button"
                        className="inline-flex items-center gap-x-0.5 rounded-md text-sm text-gray-600"
                    >
                        <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-4 rtl:rotate-180" />
                        {t('Back')}
                    </button>
                    <h3 className="text-lg font-bold leading-7 text-primary pb-1">
                        {isViewMode ? t('View Check-In Details') : t('Check-In Management')}
                    </h3>
                </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-6">
                {/* Tab Navigation */}
                <div className="border-b border-gray-200 mb-6">
                    <nav aria-label="Tabs" className="-mb-px flex space-x-8">
                        {tabs.map(tab => (
                            <button
                                key={tab.key}
                                onClick={() => handleTabClick(tab.key)}
                                disabled={tab.disabled}
                                className={`whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium ${
                                    activeTab === tab.key
                                        ? 'border-primary text-primary'
                                        : tab.disabled
                                        ? 'border-transparent text-gray-300 cursor-not-allowed'
                                        : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                                }`}
                            >
                                {tab.name}
                            </button>
                        ))}
                    </nav>
                </div>
                
                {/* Trip Information Section */}
                {currentSegment && (
                    <div className="mb-6 bg-[#F4F7F8] rounded-sm p-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 text-center text-sm gap-y-4 md:gap-y-0 text-gray-600">
                            <div className="flex">
                                <BoatIcon className="w-[20px] h-[20px] fill-[#285f74]" />
                                <div className="ps-3 text-start">
                                    <p className="pb-1">{t('From Location')}</p>
                                    <p className="font-bold text-[16px]">
                                        {currentSegment?.start?.location?.name?.en || 'N/A'}
                                    </p>
                                            </div>
                                        </div>
                            <div className="flex">
                                <BoatIcon className="w-[20px] h-[20px] fill-[#285f74]" />
                                <div className="ps-3 text-start">
                                    <p className="pb-1">{t('To Location')}</p>
                                    <p className="font-bold text-[16px]">
                                        {currentSegment?.end?.location?.name?.en}
                                    </p>
                                </div>
                                            </div>
                            <div className="flex">
                                <CalendarIcon className="w-5 h-5 mb-1 text-[#285f74]" />
                                <div className="ps-3 text-start">
                                    <p className="pb-1">{t('Travel Date')}</p>
                                    <p className="font-bold text-[15px]">
                                        {customDateFormat(currentSegment?.travel_date)}{' '}
                                        {customTimeFormat(currentSegment?.start?.departure_time)}
                                    </p>
                                            </div>
                                        </div>
                                        </div>
                                    </div>
                )}

                {/* Passengers Section */}
                {activeTab === 'passengers' && currentSegment && (
                    <div className="mb-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">
                            {t('Passengers')}
                            {isViewMode && (
                                <span className="ml-2 text-sm font-normal text-gray-500">
                                    ({t('View Only')})
                                </span>
                            )}
                        </h3>
                        <div className="space-y-3">
                            {currentSegment.passengers.map((person) => (
                                            <PassengerCard
                                                key={person.passenger_id}
                                                person={person}
                                                isSelected={selectedPassengers.includes(person.passenger_id)}
                                                luggageCount={luggageCounts[person.passenger_id] || 0}
                                                onToggle={isViewMode ? () => {} : handlePassengerToggle}
                                                onLuggageChange={isViewMode ? () => {} : handleLuggageChange}
                                                disabled={isViewMode}
                                            />
                                        ))}
                                    </div>
                                </div>
                )}

                {/* Check-in Details */}
                {activeTab === 'passengers' && (
                    <div className="mb-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('Check-In Details')}</h3>
                        
                        {/* Counter Number */}
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t('Counter Number')}
                            </label>
                            <input
                                type="text"
                                value={counterNo}
                                onChange={(e) => setCounterNo(e.target.value)}
                                disabled={isViewMode}
                                className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                placeholder={t('Enter counter number')}
                            />
                        </div>
                        
                        {/* Remarks */}
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t('Remarks')}
                            </label>
                            <textarea
                                value={remarks}
                                onChange={(e) => setRemarks(e.target.value)}
                                disabled={isViewMode}
                                rows={3}
                                className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                placeholder={t('Enter any remarks or notes')}
                            />
                        </div>
                    </div>
                )}

                {/* Luggage Details */}
                {activeTab === 'luggage' && checkinResults && (
                    <div className="mb-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold text-gray-900">{t('Luggage Information')}</h3>
                            
                            {/* Add Luggage Button */}
                            {checkinResults?.some(checkin => checkin.status?.code !== 'CHK') && (
                                <button
                                    onClick={() => {
                                        // Find first check-in to add luggage to
                                        const firstCheckin = checkinResults?.[0]
                                        if (firstCheckin && bookingId) {
                                            addLuggage({
                                                variables: {
                                                    booking_id: bookingId,
                                                    checkin_id: firstCheckin.checkin_id
                                                }
                                            })
                                        }
                                    }}
                                    disabled={addLuggageLoading || checkinResults?.every(checkin => checkin.status?.code === 'CHK')}
                                    className="relative inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-primary shadow-sm hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {addLuggageLoading ? (
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    ) : (
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                        </svg>
                                    )}
                                    {t('Add Luggage')}
                                </button>
                            )}
                        </div>
                        
                        {/* Individual Passenger Luggage */}
                        <div className="space-y-6">
                            {checkinResults.map((checkin) => (
                                <div key={checkin.checkin_id} className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 shadow-sm">
                                    {/* Passenger Header */}
                                    <div className="mb-6">
                                        <div className="flex items-center gap-3 mb-3">
                                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                                                <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                </svg>
                                            </div>
                                            <h5 className="text-base font-semibold text-gray-900">
                                                {findPassengerById(checkin.passenger_id)?.passenger?.given_name} {findPassengerById(checkin.passenger_id)?.passenger?.family_name}
                                            </h5>
                                        </div>
                                        
                                        <div className="flex flex-wrap items-center gap-4 pl-13">
                                            <div className="flex items-center gap-2">
                                                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                                </svg>
                                                <span className="text-sm text-gray-600">{t('Luggage Count')}:</span>
                                                <span className="font-semibold text-primary text-sm">{checkin.luggages?.length || 0}</span>
                                            </div>
                                            <div className="h-4 w-px bg-gray-300"></div>
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm text-gray-600">{t('Check-in Status')}:</span>
                                                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                                                    checkin.status?.code === 'CHK' ? 'bg-green-50 text-green-700 ring-1 ring-green-600/20' : 
                                                    checkin.status?.code === 'CRT' ? 'bg-yellow-50 text-yellow-700 ring-1 ring-yellow-600/20' :
                                                    'bg-gray-50 text-gray-700 ring-1 ring-gray-600/20'
                                                }`}>
                                                    {checkin.status?.description?.en || checkin.status?.code || 'Pending'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Luggage Items */}
                                    {checkin.luggages && checkin.luggages.length > 0 && (
                                        <div className="space-y-4">
                                            {checkin.luggages.map((luggage, index) => (
                                                <LuggageDetailsForm
                                                    key={luggage.luggage_id}
                                                    luggage={luggage}
                                                    index={index}
                                                    luggageDetails={luggageDetails}
                                                    luggageTags={luggageTags}
                                                    updateLuggageLoading={updateLuggageLoading && updatingLuggageId === luggage.luggage_id}
                                                    addLuggageLoading={addLuggageLoading}
                                                    removeLuggageLoading={removeLuggageLoading && removingLuggageId === luggage.luggage_id}
                                                    printLuggageLoading={printLuggageLoading && printingLuggageId === luggage.luggage_id}
                                                    onLuggageDetailsChange={handleLuggageDetailsChange}
                                                    onUpdateLuggage={handleUpdateLuggage}
                                                    onPrintLuggage={handlePrintLuggage}
                                                    onAddLuggage={handleAddLuggage}
                                                    onRemoveLuggage={handleRemoveLuggage}
                                                    isCheckedIn={checkin.status?.code === 'CHK'}
                                                    savedLuggageDetails={savedLuggageDetails}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}


                {/* Action Buttons */}
                <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
                    <button
                        onClick={handleBack}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                    >
                        {activeTab === 'passengers' ? (isViewMode ? t('Back') : t('Cancel')) : t('Back')}
                    </button>
                    
                    {activeTab === 'passengers' && !isViewMode && (
                        <button
                            onClick={handleStartCheckIn}
                            disabled={isLoading || selectedPassengers.length === 0}
                            className="flex items-center gap-2 px-6 py-2 text-sm font-medium text-white bg-primary border border-transparent rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isLoading ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    {t('Processing...')}
                                </>
                            ) : (
                                <>
                                    <CheckIcon className="w-4 h-4" />
                                    {t('Start Check-In')}
                                </>
                            )}
                        </button>
                    )}
                    
                    {activeTab === 'luggage' && !isCheckedIn && (
                        <button
                            onClick={handleCompleteCheckIn}
                            disabled={completeCheckinLoading || !allLuggageAccepted || isCheckedIn}
                            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary border border-transparent rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {completeCheckinLoading ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    {t('Processing...')}
                                </>
                            ) : (
                                <>
                                    <CheckCircleIcon className="w-4 h-4" />
                                    {t('Complete Check-In')}
                                </>
                            )}
                        </button>
                    )}
                </div>
            </div>

            {/* Success Dialog */}
            {showSucDialog && (
                <SuccessDialog
                    status={true}
                    title={t('Success')}
                    message={dialogMessages}
                    onClose={() => setShowSucDialog(false)}
                />
            )}

            {/* Error Dialog */}
            {showErrDialog && (
                <ErrorDialog
                    status={true}
                    title={t('Error')}
                    message={dialogMessages}
                    onClose={() => setShowErrDialog(false)}
                />
            )}

            {/* Confirmation Dialog */}
            {showConfirmDialog && (
                <ConfirmDialog
                    status={true}
                    title={t('Confirm Check-In Completion')}
                    message={t('Are you sure you want to finalize this check-in? This action will complete the check-in process for all passengers and cannot be undone.')}
                    negativeActionText={t('Cancel')}
                    positiveActionText={t('Yes, Finalize')}
                    onPositive={confirmCompleteCheckin}
                    onNegative={() => setShowConfirmDialog(false)}
                />
            )}

            {/* Remove Luggage Confirmation Dialog */}
            {showRemoveConfirmDialog && (
                <ConfirmDialog
                    status={true}
                    title={t('Confirm Remove Luggage')}
                    message={t('Are you sure you want to remove this luggage? This action cannot be undone.')}
                    negativeActionText={t('Cancel')}
                    positiveActionText={t('Yes, Remove')}
                    onPositive={confirmRemoveLuggage}
                    onNegative={() => setShowRemoveConfirmDialog(false)}
                />
            )}

            {/* Error Toast for Zebra Printer Issues */}
            {showErrToast && (
                <ErrorToast
                    status={true}
                    title={'Printer Error'}
                    message={toastMessages}
                    onClose={() => setShowErrToast(false)}
                />
            )}
        </>
    )
}
